"use client";

import { Icon } from "@/components/atoms/Icon/Icon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/molecules/DropdownMenu/DropdownMenu";
import { cn } from "@/lib/utils";
import {
  Delete02Icon,
  Folder01Icon,
  MoreHorizontalIcon,
  PencilEdit02Icon,
} from "@hugeicons/core-free-icons";

interface Props {
  folderName: string;
  onRename: () => void;
  onMove: () => void;
  onDelete: () => void;
  className?: string;
}

export function FolderActionsMenu({
  folderName,
  onRename,
  onMove,
  onDelete,
  className,
}: Props) {
  return (
    <DropdownMenu data-sentry-element="DropdownMenu" data-sentry-component="FolderActionsMenu" data-sentry-source-file="FolderActionsMenu.tsx">
      <DropdownMenuTrigger asChild data-sentry-element="DropdownMenuTrigger" data-sentry-source-file="FolderActionsMenu.tsx">
        <button
          type="button"
          aria-label={`Actions for ${folderName}`}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-foreground-tertiary transition-colors hover:bg-surface-hover hover:text-foreground-strong",
            className,
          )}
          data-testid="folder-actions-menu"
        >
          <Icon icon={MoreHorizontalIcon} size={20} data-sentry-element="Icon" data-sentry-source-file="FolderActionsMenu.tsx" />
        </button>
      </DropdownMenuTrigger>
      {/* preventDefault keeps the menu mounted while the dialog opens, so the
          menu's focus return doesn't fight the dialog for focus. */}
      <DropdownMenuContent align="end" className="w-44" data-sentry-element="DropdownMenuContent" data-sentry-source-file="FolderActionsMenu.tsx">
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            onRename();
          }}
          data-testid="folder-rename-menu"
        data-sentry-element="DropdownMenuItem" data-sentry-source-file="FolderActionsMenu.tsx">
          <Icon icon={PencilEdit02Icon} size={16} className="mr-2" data-sentry-element="Icon" data-sentry-source-file="FolderActionsMenu.tsx" />
          Rename
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            onMove();
          }}
          data-testid="folder-move-menu"
        data-sentry-element="DropdownMenuItem" data-sentry-source-file="FolderActionsMenu.tsx">
          <Icon icon={Folder01Icon} size={16} className="mr-2" data-sentry-element="Icon" data-sentry-source-file="FolderActionsMenu.tsx" />
          Move to folder
        </DropdownMenuItem>
        <DropdownMenuSeparator data-sentry-element="DropdownMenuSeparator" data-sentry-source-file="FolderActionsMenu.tsx" />
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            onDelete();
          }}
          // oxlint-disable-next-line design-system/no-restyle -- a destructive item; the menu molecule has no destructive variant
          className="text-error-hover focus:bg-error-surface focus:text-error-foreground"
          data-testid="folder-delete-menu"
        data-sentry-element="DropdownMenuItem" data-sentry-source-file="FolderActionsMenu.tsx">
          <Icon icon={Delete02Icon} size={16} className="mr-2" data-sentry-element="Icon" data-sentry-source-file="FolderActionsMenu.tsx" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
